/**
 * What survives a move between lists — tasks plan D8, the client's copy of
 * the table in `move_line()`'s comment (`20261017220000_tasks_repeat_clear.sql`).
 * Read the two against each other; the kind guard rejects a column the
 * target kind cannot hold, so a client patch that kept one would fail the
 * optimistic cache's truth on settle.
 *
 *   always:                  body, note, tags, link, entry_id, currency
 *   task → task:             due_on, repeat_every, repeat_unit
 *   grocery → grocery:       qty
 *   buy → buy:               price_cents, status, image_url
 *   collection → collection: flagged_at
 *   template → template:     section
 *   never:                   parent_id (the parent is not coming)
 *
 * Dependency-free (type-only import) for the root suite.
 */
import type { ListItem, ListKind } from './types'

type Kinded = keyof Pick<ListItem, 'due_on' | 'repeat_every' | 'repeat_unit' | 'qty' | 'price_cents' | 'status' | 'image_url' | 'flagged_at' | 'section'>

const OWNED: Record<ListKind, Kinded[]> = {
  task: ['due_on', 'repeat_every', 'repeat_unit'],
  grocery: ['qty'],
  buy: ['price_cents', 'status', 'image_url'],
  collection: ['flagged_at'],
  template: ['section'],
}

/** The patch a moved row gets: `list_id`, `parent_id` null, and every
 *  kind-owned column the target cannot hold set null. */
export function movePatch(from: ListKind, to: ListKind, toListId: string): Partial<ListItem> & { list_id: string } {
  const patch: Partial<ListItem> & { list_id: string } = { list_id: toListId, parent_id: null }
  if (from !== to) for (const col of OWNED[from]) (patch as Record<string, unknown>)[col] = null
  return patch
}

/** The columns the move drops, by name, when the row actually carries a
 *  value in them — so a toast can say `its due date was dropped`, and Undo
 *  (a move back) is offered only when nothing was lost. */
export function dropped(from: ListKind, to: ListKind, item: Partial<ListItem>): Kinded[] {
  if (from === to) return []
  return OWNED[from].filter(col => item[col] != null)
}

/** `dropped`, as words for the toast. */
export function droppedPhrase(cols: Kinded[]): string | null {
  if (!cols.length) return null
  const words: Record<Kinded, string> = {
    due_on: 'due date', repeat_every: 'repeat', repeat_unit: 'repeat', qty: 'amount',
    price_cents: 'price', status: 'status', image_url: 'picture', flagged_at: 'flag', section: 'section',
  }
  const names = [...new Set(cols.map(c => words[c]))]
  return `its ${names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`} ${names.length === 1 ? 'was' : 'were'} dropped`
}
